// DEV-ONLY local engine store — never reaches a production bundle.
//
// Why this exists: a localhost session pointed at PRODUCTION data keeps
// everything real (classes, thumbnails, masterclasses, profile) and keeps only
// the member's own engine state in this browser, so a walk-through never
// writes engine documents into the live project.
//
// STALE CLAIM CORRECTED (cutover audit 2026-09-16): this used to say the four
// subcollections were "not readable under the live Firestore rules". They have
// been readable since the rules deploy of 2026-09-12 (GO 1) — engineMeta,
// engineExpressions, engineSituations, engineDeployments and engineEvents all
// carry owner-or-admin blocks in firestore.rules. What this file buys now is
// isolation, not access.
//
// WHAT IT COSTS, and it matters: a build made with VUE_APP_ENGINE_LOCAL=true
// does NOT exercise the real read/write path. Walking such a build proves the
// interface, never the data. The Firestore path is covered by the e2e suites,
// which run against the emulator with ENGINE_LOCAL unset.
//
// Same method surface as engineStateService, same return shapes. Backed by
// localStorage and namespaced per uid, so switching accounts is clean.
import { markEngineDirty } from '@/services/engineBus';

const KEY = (uid) => `yp.engineLocal.${uid}`;

// Dev convenience: visiting any engine page with ?engineReset=1 wipes this
// browser's engine state, so a drill can be re-run from scratch the same day
// instead of waiting for tomorrow. Local review only — nothing server-side.
try {
  if (typeof location !== 'undefined' && /[?&]engineReset=1/.test(location.search)) {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('yp.engineLocal.') || k.startsWith('yp.clipPos.') || k.startsWith('yp.engineWallLocal') || k.startsWith('engineWallLocal') || k.startsWith('engineDeployLocal'))
      .forEach((k) => localStorage.removeItem(k));
    // eslint-disable-next-line no-console
    console.warn('[engine] LOCAL engine state cleared (?engineReset=1)');
    // Strip the param so a mid-walk browser refresh does NOT wipe again —
    // reset is a one-shot, not a standing property of the tab.
    const cleaned = location.search.replace(/([?&])engineReset=1&?/, '$1').replace(/[?&]$/, '');
    window.history.replaceState(null, '', location.pathname + cleaned + location.hash);
  }
} catch (e) { /* private mode — nothing to clear */ }

function load(uid) {
  try {
    return JSON.parse(localStorage.getItem(KEY(uid)) || '') || null;
  } catch (e) {
    return null;
  }
}
function blank() {
  return { meta: null, states: {}, situations: {}, events: [] };
}
function save(uid, db) {
  try {
    localStorage.setItem(KEY(uid), JSON.stringify(db));
  } catch (e) { /* quota — local review only, nothing to recover */ }
  markEngineDirty();
  return true;
}
function read(uid) {
  return load(uid) || blank();
}

export default {
  /** Mirrors engineStateService.fetchAll — null means "do not start a session". */
  async fetchAll(uid) {
    if (!uid) return null;
    const db = read(uid);
    return { meta: db.meta, states: db.states || {}, situations: db.situations || {} };
  },

  async ensureMeta(uid, { timezone }) {
    const db = read(uid);
    if (!db.meta) {
      db.meta = {
        firstRunDone: false,
        flipWelcomeSeen: false,
        timezone: timezone || null,
        streak: { current: 0, longest: 0, lastActiveDate: null, freezesRemaining: 0 },
        session: { inProgress: null, lastCompletedDate: null, lastCompletedCount: 0 },
      };
      save(uid, db);
    }
    return db.meta;
  },

  async saveInProgress(uid, inProgress) {
    const db = read(uid);
    db.meta = db.meta || {};
    db.meta.session = { ...(db.meta.session || {}), inProgress };
    return save(uid, db);
  },

  async clearInProgress(uid) {
    return this.saveInProgress(uid, null);
  },

  async commitOutcome(uid, c) {
    if (!uid || !c || !c.recId || !c.nextState) return false;
    const db = read(uid);
    db.states[c.recId] = { ...c.nextState, updatedAt: Date.now() };
    if (c.situationId && c.situationDoc) {
      const prev = db.situations[c.situationId] || {};
      // The playlist belongs to the clip layer — never clobber it here.
      db.situations[c.situationId] = { ...c.situationDoc, playlist: prev.playlist, updatedAt: Date.now() };
    }
    if (c.metaPatch) db.meta = { ...(db.meta || {}), ...c.metaPatch };
    if (c.event && c.event.type) db.events.push({ v: 1, ...c.event, loggedAt: Date.now() });
    return save(uid, db);
  },

  async patchMeta(uid, patch) {
    const db = read(uid);
    db.meta = { ...(db.meta || {}), ...patch };
    return save(uid, db);
  },

  async patchSituationPlaylist(uid, c) {
    const db = read(uid);
    const prev = db.situations[c.situationId] || {};
    const next = { ...prev, playlist: c.playlist };
    if (c.completedAt) next.completedAt = c.completedAt;
    if (c.masteredAt) next.masteredAt = c.masteredAt;
    db.situations[c.situationId] = next;
    if (c.event && c.event.type) db.events.push({ v: 1, ...c.event, loggedAt: Date.now() });
    return save(uid, db);
  },

  async patchSituation(uid, c) {
    if (!uid || !c || !c.situationId || !c.patch) return false;
    const db = read(uid);
    db.situations[c.situationId] = {
      ...(db.situations[c.situationId] || {}),
      ...c.patch,
      updatedAt: Date.now(),
    };
    if (c.event && c.event.type) db.events.push({ v: 1, ...c.event, loggedAt: Date.now() });
    return save(uid, db);
  },

  async patchExpression(uid, c) {
    if (!uid || !c || !c.recId || !c.patch) return false;
    const db = read(uid);
    db.states[c.recId] = {
      ...(db.states[c.recId] || {}),
      ...c.patch,
      updatedAt: Date.now(),
    };
    if (c.event && c.event.type) db.events.push({ v: 1, ...c.event, loggedAt: Date.now() });
    return save(uid, db);
  },
};
